import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Upload, FileText, Building2, CheckCircle, Loader2 } from 'lucide-react';
import { EnterpriseOpcoDetection } from './EnterpriseOpcoDetection';
import { formatPriceFromCents } from '@/utils/formatPrice';

interface FinancingRequestFormProps {
  courseId: string;
  formationTitle: string;
  amount: number;
  currency: string;
  learnerCount?: number;
  isEnterprise?: boolean;
  isEmployeeRegistration?: boolean;
  multiLearnerData?: {
    learners: Array<{
      firstName: string;
      lastName: string;
      email: string;
      phone?: string;
      position?: string;
    }>;
    company: any;
    startDate?: Date;
  };
  fallbackUser?: { id: string; email?: string | null } | null;
  onSuccess: () => void;
}

interface FormData {
  nom: string;
  prenom: string;
  email: string;
  telephone: string;
  fonction: string;
  nomOrganisme: string;
  typeFinancement: string;
  commentaire: string;
}

export function FinancingRequestForm({ 
  courseId, 
  formationTitle, 
  amount, 
  currency, 
  learnerCount = 1,
  isEnterprise = false,
  isEmployeeRegistration = false,
  multiLearnerData,
  fallbackUser,
  onSuccess 
}: FinancingRequestFormProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [participantCount, setParticipantCount] = useState(learnerCount);
  
  // Pré-remplir avec les données déjà collectées dans le tunnel
  const getInitialFormData = (): FormData => {
    if (multiLearnerData?.company) {
      const company = multiLearnerData.company;
      // L'organisme de financement est l'OPCO, pas l'entreprise
      const opcoName = company.opco?.opco || company.suggestedOpco || '';
      return {
        nom: company.contactLastName || company.contactNom || '',
        prenom: company.contactFirstName || company.contactPrenom || '',
        email: company.contactEmail || '',
        telephone: company.contactPhone || company.contactTel || '',
        fonction: company.contactPosition || company.contactFonction || '',
        nomOrganisme: opcoName, // C'est l'OPCO qui est l'organisme de financement
        typeFinancement: 'opco',
        commentaire: ''
      };
    }
    return {
      nom: '',
      prenom: '',
      email: '',
      telephone: '',
      fonction: '',
      nomOrganisme: '',
      typeFinancement: 'opco', // Toujours OPCO car ce formulaire est dédié aux demandes OPCO
      commentaire: ''
    };
  };
  
  const [formData, setFormData] = useState<FormData>(getInitialFormData);
  
  // Déterminer si on doit afficher la détection OPCO
  // Si le SIRET est déjà fourni, on peut directement lancer la détection
  const hasCompanyData = !!(multiLearnerData?.company?.siret || multiLearnerData?.company?.siren);
  const [showOpcoDetection, setShowOpcoDetection] = useState(!hasCompanyData);
  
  // Initialiser l'OPCO détecté depuis les données du tunnel si disponible
  const initialOpcoName = multiLearnerData?.company?.opco?.opco || multiLearnerData?.company?.suggestedOpco || '';
  const [detectedOpco, setDetectedOpco] = useState<any>(
    initialOpcoName ? { opco: initialOpcoName, confidence: 'high' } : null
  );
  const [companyData, setCompanyData] = useState<any>(
    hasCompanyData ? {
      denomination: multiLearnerData?.company?.companyName || multiLearnerData?.company?.denomination,
      siret: multiLearnerData?.company?.siret,
      siren: multiLearnerData?.company?.siren,
      code_naf: multiLearnerData?.company?.codeNaf || multiLearnerData?.company?.code_naf,
      libelle_naf: multiLearnerData?.company?.libelleNaf || multiLearnerData?.company?.libelle_naf,
      adresse: multiLearnerData?.company?.address || multiLearnerData?.company?.adresse,
      suggested_opco: initialOpcoName
    } : null
  );
  
  const { user } = useAuth();
  const effectiveUser = user || fallbackUser;
  const { toast } = useToast();

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Auto-détecter l'OPCO si le SIRET est déjà connu
  const [isAutoDetecting, setIsAutoDetecting] = useState(false);
  
  useEffect(() => {
    const autoDetectOpco = async () => {
      if (hasCompanyData && multiLearnerData?.company?.siret && !detectedOpco) {
        setIsAutoDetecting(true);
        try {
          console.log('🔍 Auto-détection OPCO pour SIRET:', multiLearnerData.company.siret);
          const { data, error } = await supabase.functions.invoke('sirene-integration', {
            body: { 
              action: 'search_siren',
              siret: multiLearnerData.company.siret 
            }
          });
          
          if (!error && data?.success && data?.data) {
            const companyInfo = data.data;
            console.log('✅ OPCO détecté automatiquement:', companyInfo);
            setCompanyData(companyInfo);
            
            if (companyInfo.suggested_opco) {
              setDetectedOpco({ 
                opco: companyInfo.suggested_opco, 
                confidence: 'high' 
              });
              setFormData(prev => ({
                ...prev,
                nomOrganisme: companyInfo.suggested_opco,
                typeFinancement: 'opco'
              }));
            }
            setShowOpcoDetection(false);
          } else if (error) {
            console.warn('Erreur API sirene-integration:', error);
          } else {
            console.warn('Format de réponse inattendu:', data);
          }
        } catch (err) {
          console.warn('Auto-détection OPCO échouée:', err);
        } finally {
          setIsAutoDetecting(false);
        }
      }
    };
    
    autoDetectOpco();
  }, [hasCompanyData, multiLearnerData?.company?.siret, detectedOpco]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!effectiveUser) {
      toast({
        title: "Erreur",
        description: "Vous devez être connecté pour faire une demande",
        variant: "destructive",
      });
      return;
    }

    // Validation des champs obligatoires
    const requiredFields = ['nom', 'prenom', 'email', 'nomOrganisme', 'typeFinancement'];
    const missingFields = requiredFields.filter(field => !formData[field as keyof FormData]);
    
    if (missingFields.length > 0) {
      toast({
        title: "Champs manquants",
        description: "Veuillez remplir tous les champs obligatoires",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-financing-request', {
        body: {
          courseId,
          formationTitle,
          amount: amount,
          currency,
          participantCount,
          userId: effectiveUser.id,
          userEmail: effectiveUser.email,
          ...formData
        }
      });

      if (error) throw error;

      toast({
        title: "Demande envoyée !",
        description: "Votre demande de financement a été enregistrée.",
      });
      
      // Redirect to success page with demande info
      const demandeId = data?.demandeFinancementId || '';
      const inscriptionId = data?.inscriptionId || '';
      navigate(`/financement/success?demande_id=${demandeId}&inscription_id=${inscriptionId}`);
      
      onSuccess();
    } catch (error) {
      console.error('Erreur demande financement:', error);
      toast({
        title: "Erreur",
        description: "Impossible d'envoyer la demande. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Informations personnelles */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Responsable de la demande</CardTitle>
          <CardDescription>
            Informations du responsable au sein de l'entreprise qui effectue cette demande de financement
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="prenom">Prénom *</Label>
              <Input
                id="prenom"
                value={formData.prenom}
                onChange={(e) => handleInputChange('prenom', e.target.value)}
                placeholder="Votre prénom"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="nom">Nom *</Label>
              <Input
                id="nom"
                value={formData.nom}
                onChange={(e) => handleInputChange('nom', e.target.value)}
                placeholder="Votre nom"
                required
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="votre.email@exemple.com"
              required
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="telephone">Téléphone</Label>
              <Input
                id="telephone"
                value={formData.telephone}
                onChange={(e) => handleInputChange('telephone', e.target.value)}
                placeholder="06 12 34 56 78"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fonction">Fonction</Label>
              <Input
                id="fonction"
                value={formData.fonction}
                onChange={(e) => handleInputChange('fonction', e.target.value)}
                placeholder="Votre fonction"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Détection automatique de l'OPCO */}
      {isAutoDetecting ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              Identification de votre entreprise...
            </CardTitle>
            <CardDescription>
              Nous récupérons automatiquement les informations de votre OPCO
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center py-8">
              <div className="text-center space-y-2">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
                <p className="text-sm text-muted-foreground">Détection en cours...</p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : showOpcoDetection ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Identification de votre entreprise et OPCO
            </CardTitle>
            <CardDescription>
              Trouvez votre OPCO pour bénéficier d'un financement professionnel
            </CardDescription>
          </CardHeader>
          <CardContent>
            <EnterpriseOpcoDetection
              onOpcoDetected={(opco, company) => {
                setDetectedOpco(opco);
                setCompanyData(company);
                setFormData(prev => ({
                  ...prev,
                  nomOrganisme: company || opco,
                  typeFinancement: 'opco'
                }));
                setShowOpcoDetection(false);
              }}
              onContinueWithoutOpco={() => setShowOpcoDetection(false)}
            />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              {detectedOpco && <CheckCircle className="h-5 w-5 text-green-600" />}
              Organisme de financement
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {detectedOpco && companyData && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="font-semibold text-green-800">OPCO détecté automatiquement</span>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Entreprise :</span>
                    <p className="font-semibold">{companyData.denomination}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">OPCO :</span>
                    <p className="font-semibold text-blue-600">{detectedOpco.opco}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Code NAF :</span>
                    <p>{companyData.code_naf} - {companyData.libelle_naf}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Confiance :</span>
                    <p className={`font-semibold ${detectedOpco.confidence === 'high' ? 'text-green-600' : 'text-orange-600'}`}>
                      {detectedOpco.confidence === 'high' ? 'Élevée' : 'Moyenne'}
                    </p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setShowOpcoDetection(true);
                    setDetectedOpco(null);
                    setCompanyData(null);
                  }}
                >
                  Modifier l'entreprise
                </Button>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="nomOrganisme">OPCO de financement *</Label>
              {formData.nomOrganisme ? (
                <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-medium text-green-800">{formData.nomOrganisme}</p>
                    <p className="text-xs text-green-600">OPCO détecté automatiquement</p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleInputChange('nomOrganisme', '')}
                    className="text-green-700 hover:text-green-900"
                  >
                    Modifier
                  </Button>
                </div>
              ) : (
                <Input
                  id="nomOrganisme"
                  value={formData.nomOrganisme}
                  onChange={(e) => handleInputChange('nomOrganisme', e.target.value)}
                  placeholder="Saisissez le nom de votre OPCO..."
                  required
                />
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Détails de la formation */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Formation sélectionnée</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <h4 className="font-semibold">{formationTitle}</h4>
            <p className="text-2xl font-bold text-primary">
              {formatPriceFromCents(amount, currency)}
            </p>
            {participantCount > 1 && (
              <p className="text-sm text-muted-foreground">
                Pour {participantCount} participant{participantCount > 1 ? 's' : ''}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Commentaires et documents */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Informations complémentaires</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="commentaire">Commentaires ou demandes spécifiques</Label>
            <Textarea
              id="commentaire"
              value={formData.commentaire}
              onChange={(e) => handleInputChange('commentaire', e.target.value)}
              placeholder="Précisions sur votre demande, délais, modalités..."
              rows={4}
            />
          </div>
          
          {/* Information sur le processus OPCO - toujours affiché car ce formulaire est dédié aux demandes OPCO */}
          {true && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Processus de financement OPCO
              </h4>
              <div className="space-y-2 text-sm text-blue-800">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span><strong>Étape 1 :</strong> Remplissage de ce formulaire (en cours)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
                  <span><strong>Étape 2 :</strong> Envoi de la convention de formation par email</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
                  <span><strong>Étape 3 :</strong> Signature électronique de la convention</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
                  <span><strong>Étape 4 :</strong> Envoi de la demande par vos soins à votre contact OPCO</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
                  <span><strong>Étape 5 :</strong> Réponse OPCO (accord total/partiel)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
                  <span><strong>Étape 6 :</strong> Paiement reliquat (si nécessaire) et accès formation</span>
                </div>
              </div>
              <div className="mt-3 p-2 bg-blue-100 rounded text-xs text-blue-700">
                <strong>Note :</strong> Aucun document n'est requis à cette étape. 
                Nous vous fournirons tous les documents nécessaires pour transmettre à votre OPCO.
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Boutons */}
      <div className="flex gap-4">
        <Button
          type="button"
          variant="outline"
          onClick={onSuccess}
          className="flex-1"
        >
          Annuler
        </Button>
        <Button
          type="submit"
          disabled={loading || !isFormValid()}
          className="flex-1 btn-primary"
        >
          {loading ? 'Envoi en cours...' : 'Envoyer la demande'}
        </Button>
      </div>
    </form>
  );

  // Fonction de validation du formulaire
  function isFormValid(): boolean {
    return !!(
      formData.nom.trim() && 
      formData.prenom.trim() && 
      formData.email.trim() && 
      formData.nomOrganisme.trim()
    );
  }
}